import React from 'react';
import { Eye, Send, CheckCircle, Play, Trash2, Calendar, Package } from 'lucide-react';


const BatchListTable = ({
  batches,
  onViewBatch,
  onSubmitForReview,
  onApproveBatch,
  onStartProcessing,
  onDeleteBatch
}) => {
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      pending_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return badges?.[status] || badges?.draft;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      critical: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return badges?.[priority] || badges?.medium;
  };

  if (!batches || batches?.length === 0) {
    return (
      <div className="p-12 text-center">
        <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No renewal batches found</p>
        <p className="text-sm text-gray-500 mt-2">Create a new batch or use auto-create to get started</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Batch Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Priority
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Documents
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Progress
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Expiration Window
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {batches?.map((batch) => (
            <tr key={batch?.id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <div>
                  <div className="text-sm font-medium text-gray-900">{batch?.batchName}</div>
                  {batch?.batchDescription && (
                    <div className="text-sm text-gray-500">{batch?.batchDescription}</div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadge(batch?.batchStatus)}`}>
                  {batch?.batchStatus?.replace('_', ' ')?.toUpperCase()}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getPriorityBadge(batch?.batchPriority)}`}>
                  {batch?.batchPriority?.toUpperCase()}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {batch?.processedDocuments || 0} / {batch?.totalDocuments || 0}
                </div>
                {batch?.failedDocuments > 0 && (
                  <div className="text-xs text-red-600">
                    {batch?.failedDocuments} failed
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className="bg-emerald-600 h-2 rounded-full"
                      style={{ width: `${batch?.workflowProgressPercentage || 0}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600">
                    {Math.round(batch?.workflowProgressPercentage || 0)}%
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center text-sm text-gray-900">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  <div>
                    <div>{formatDate(batch?.expirationWindowStart)}</div>
                    <div className="text-xs text-gray-500">to {formatDate(batch?.expirationWindowEnd)}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onViewBatch(batch)}
                    className="text-blue-600 hover:text-blue-900"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {batch?.batchStatus === 'draft' && (
                    <button
                      onClick={() => onSubmitForReview(batch?.id)}
                      className="text-green-600 hover:text-green-900"
                      title="Submit for Review"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  )}
                  {batch?.batchStatus === 'pending_review' && (
                    <button
                      onClick={() => onApproveBatch(batch?.id)}
                      className="text-green-600 hover:text-green-900"
                      title="Approve Batch"
                    >
                      <CheckCircle className="h-4 w-4" />
                    </button>
                  )}
                  {batch?.batchStatus === 'approved' && (
                    <button
                      onClick={() => onStartProcessing(batch?.id)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Start Processing"
                    >
                      <Play className="h-4 w-4" />
                    </button>
                  )}
                  {(batch?.batchStatus === 'draft' || batch?.batchStatus === 'cancelled') && (
                    <button
                      onClick={() => onDeleteBatch(batch?.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Batch"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BatchListTable;